import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import PageHeader from '../../../components/Layout/PageHeader.jsx';
import ChoferForm from '../../../components/ChoferForm.jsx';
import { SkeletonForm } from '../../../components/ui/StateComponents.jsx';
import { Button } from '../../../components/ui/FormComponents.jsx';
import { useChoferById, useChoferMutations } from '../../../hooks/useChoferes.js';
import { uploadChoferDocuments } from '../../../services/choferDocumentos.js';

function licenseStatus(licenciaVencimiento) {
  if (!licenciaVencimiento) return null;
  const date = new Date(licenciaVencimiento);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) {
    return {
      tone: 'critical',
      title: 'Licencia vencida',
      description: 'La licencia del chofer está vencida. Marcá el estado como suspendido o actualizá la fecha.',
    };
  }
  if (diff <= 30) {
    return {
      tone: 'warning',
      title: 'Licencia próxima a vencer',
      description: `La licencia vence en ${diff} día${diff === 1 ? '' : 's'}. Asegurate de actualizar la documentación.`,
    };
  }
  return null;
}

export default function EditarChofer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [{ data, fetching, error }, refetch] = useChoferById(id);
  const { update, softDelete, restore, bulkAction } = useChoferMutations();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const chofer = data?.chofer;

  const banner = useMemo(() => licenseStatus(chofer?.licenciaVencimiento), [chofer?.licenciaVencimiento]);

  const handleSubmit = async values => {
    if (!id) return;
    setSaving(true);
    setFormError(null);
    try {
      const input = {
        ...values,
        aptoPsicofisico: Boolean(values.aptoPsicofisico),
        docOtros: values.docOtros?.filter(Boolean) ?? [],
      };
      delete input.__documentFiles;
      await update(id, input);
      await uploadChoferDocuments(id, values.__documentFiles);
      await Swal.fire('Cambios guardados', 'El chofer se actualizó correctamente.', 'success');
      refetch({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      setFormError(mutationError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEstado = async () => {
    if (!id || !chofer) return;
    const action = chofer.estado === 'ACTIVO' ? 'SUSPENDER' : 'ACTIVAR';
    const confirmed = await Swal.fire({
      title: action === 'SUSPENDER' ? '¿Suspender chofer?' : '¿Activar chofer?',
      text:
        action === 'SUSPENDER'
          ? 'El chofer no podrá asignarse a viajes hasta que lo reactives.'
          : 'El chofer quedará disponible para asignaciones.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#009688',
      cancelButtonColor: '#64748b',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await bulkAction([id], action);
      await Swal.fire('Actualizado', 'El estado del chofer se modificó.', 'success');
      refetch({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const handleSoftDelete = async () => {
    if (!id) return;
    const confirmed = await Swal.fire({
      title: '¿Eliminar chofer?',
      text: 'Se realizará un borrado lógico. Podrás restaurarlo luego.',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirmed.isConfirmed) return;
    try {
      await softDelete(id);
      await Swal.fire('Chofer eliminado', 'Quedará oculto del listado principal.', 'success');
      navigate('/administracion/choferes');
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const handleRestore = async () => {
    if (!id) return;
    try {
      await restore(id);
      await Swal.fire('Chofer restaurado', 'El chofer vuelve a estar disponible.', 'success');
      refetch({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      await Swal.fire('Error', mutationError.message, 'error');
    }
  };

  const headerActions = chofer ? (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="secondary" onClick={handleToggleEstado}>
        {chofer.estado === 'ACTIVO' ? 'Suspender' : 'Activar'}
      </Button>
      {chofer.deletedAt ? (
        <Button type="button" variant="secondary" onClick={handleRestore}>
          Restaurar
        </Button>
      ) : (
        <Button type="button" variant="secondary" onClick={handleSoftDelete}>
          Eliminar
        </Button>
      )}
    </div>
  ) : null;

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 px-4 pb-24">
      <PageHeader
        title={chofer ? chofer.nombreCompleto : 'Editar chofer'}
        subtitle="Actualizá los datos del chofer, su documentación y su vigencia operativa."
        breadcrumbs={[
          { label: 'Administración', href: '/administracion' },
          { label: 'Choferes', href: '/administracion/choferes' },
          { label: chofer ? chofer.nombreCompleto : 'Detalle' },
        ]}
        actions={headerActions}
      />

      {banner ? (
        <div
          className={`rounded-2xl border px-6 py-4 text-sm ${
            banner.tone === 'critical'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200'
              : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200'
          }`}
        >
          <p className="font-semibold">{banner.title}</p>
          <p className="mt-1 text-xs opacity-90">{banner.description}</p>
        </div>
      ) : null}

      {formError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-semibold">No pudimos guardar los cambios.</p>
          <p className="mt-1 text-xs opacity-90">{formError}</p>
        </div>
      ) : null}

      {fetching && !chofer ? (
        <SkeletonForm fields={12} />
      ) : chofer ? (
        <ChoferForm
          defaultValues={{
            ...chofer,
            aptoPsicofisico: Boolean(chofer.aptoPsicofisico),
            docOtros: chofer.docOtros ?? [],
            licenciaClase: chofer.licenciaClase ?? [],
          }}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/administracion/choferes')}
          saving={saving}
        />
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-semibold">No se encontró el chofer.</p>
          <p className="mt-1 text-xs opacity-90">{error.message}</p>
        </div>
      ) : null}
    </div>
  );
}
